import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Button } from '@ferris/react/components/button';
import { hot } from 'react-hot-loader';
import { TrackingContext } from '@vrbo/react-event-tracking';
import TranslationUtil from '../../utils/TranslationUtil';
import ProgressBarLYP from '../ProgressBarLYP';
import AppActions from '../../actions/app';
import withPageFlow from '../PageFlow/PageFlow';
import log from '../../utils/ClientLogger';
import { getLinks } from '../../utils/LocaleMap';
import InputStepper from './InputStepper';
import { capitalizeBrand } from '../../utils/BrandUtil';
import { createPageViewEvent } from '../../utils/EventProviders/EgClickstreamUtil';
import ExperimentUtil from '../../utils/ExperimentUtil';
import { isContentMessagingUpdatesEnabled, isRebrandingActive } from '../../utils/ExperimentsUtil';

export const BedAndBath = (props) => {
    const {
        buttonNextLabel,
        bedroomCount,
        bathroomCount,
        bathroomCountStepSize,
        referrer,
        setBathroomCount,
        setBedroomCount,
        translations,
        pageFlow,
        templateId,
        brandName,
        contentMessagingUpdatesEnabled,
        isRebranded
    } = props;

    const [moveout, setMoveout] = useState(false);

    const { trigger } = useContext(TrackingContext);

    useEffect(() => {
        trigger('pageLoad', { pageName: 'partner-qualifier: property-details', referrer });
        trigger('pageview', createPageViewEvent('Propertydetails', `Propertydetails.${templateId}`));

        return () => {
            trigger('pageUnload', { referrer });
        };
    }, []);

    useEffect(() => {
        if (moveout) {
            log('info', 'bed and bath next clicked', { bathroomCount, bedroomCount });
            trigger('genericClick', {
                category: 'navigation',
                action: 'property-details: nextButton'
            });
            pageFlow.next();
        }
    }, [moveout]);

    const handleNextClick = () => {
        setMoveout(true);
    };

    const handleBedCountChange = (count) => {
        setBedroomCount(count);
        trigger('genericClick', {
            category: 'interaction',
            action: 'change-bed-count'
        });
    };

    const handleBathCountChange = (count) => {
        setBathroomCount(count);
        trigger('genericClick', {
            category: 'interaction',
            action: 'change-bath-count'
        });
    };

    const css = classnames('bed-and-bath', 'ulx-animated', 'ulx-animated--movein', {
        'ulx-animated--moveout': moveout
    });

    const bathroomMax = 10 - bathroomCountStepSize;

    return (
        <div className={css}>
            <ProgressBarLYP step={1} />
            <h2 className="bed-and-bath__title">{translations.getTranslation(contentMessagingUpdatesEnabled ? 'Vrbo.lyp.bedandbath.titleTest' : 'Vrbo.lyp.bedandbath.title')}</h2>
            <p className="bed-and-bath__description">{translations.getTranslation(contentMessagingUpdatesEnabled ? 'Vrbo.lyp.bedandbath.descriptionTest' : 'Vrbo.lyp.bedandbath.description', { brandName })}</p>
            <InputStepper
                classVal="quantity-selector bed-and-bath__counter bed-and-bath__counter--bottom-border"
                buttonVariant="round-utility-primary"
                max={9}
                min={0}
                step={1}
                name="bedroom-count"
                handleChange={handleBedCountChange}
                value={bedroomCount}
                label={translations.getTranslation('lyp.bedandbath.counter.bedroom.label', { count: bedroomCount })}
                increaseLabel={translations.getTranslation('lyp.bedandbath.counter.bedroom.increase')}
                decreaseLabel={translations.getTranslation('lyp.bedandbath.counter.bedroom.decrease')}
                disabled={false}
                isRebranded={isRebranded}
            />
            <InputStepper
                classVal="quantity-selector bed-and-bath__counter bath__counter--bottom-border"
                buttonVariant="round-utility-primary"
                min={1}
                max={bathroomMax}
                name="bathroom-count"
                step={bathroomCountStepSize}
                handleChange={handleBathCountChange}
                value={bathroomCount}
                label={translations.getTranslation('lyp.bedandbath.counter.bathroom.label', { count: bathroomCount })}
                increaseLabel={translations.getTranslation('lyp.bedandbath.counter.bathroom.increase')}
                decreaseLabel={translations.getTranslation('lyp.bedandbath.counter.bathroom.decrease')}
                disabled={false}
                isRebranded={isRebranded}
            />

            <div className="bed-and-bath__bottom">
                <Button
                    classes="bed-and-bath__btn-next btn-fadein"
                    htmlButtonType="button"
                    type="primary"
                    variant="primary"
                    data-wdio="next-button"
                    id="propertyInfoNextBtn"
                    onClick={handleNextClick}
                >
                    {buttonNextLabel}
                </Button>
            </div>
        </div>
    );
};

BedAndBath.propTypes = {
    buttonNextLabel: PropTypes.string.isRequired,
    bedroomCount: PropTypes.number.isRequired,
    bathroomCount: PropTypes.number.isRequired,
    bathroomCountStepSize: PropTypes.number.isRequired,
    referrer: PropTypes.string.isRequired,
    setBathroomCount: PropTypes.func.isRequired,
    setBedroomCount: PropTypes.func.isRequired,
    // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
    translations: PropTypes.object,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired
    }).isRequired,
    pageFlow: PropTypes.shape({
        next: PropTypes.func.isRequired
    }).isRequired,
    brandName: PropTypes.string.isRequired,
    templateId: PropTypes.string.isRequired,
    contentMessagingUpdatesEnabled: PropTypes.bool.isRequired,
    isRebranded: PropTypes.bool.isRequired
};

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
    setBathroomCount: count => dispatch(AppActions.setBathroomCount(count)),
    setBedroomCount: count => dispatch(AppActions.setBedroomCount(count))
});

/* istanbul ignore next */
const mapStateToProps = (state) => {
    const { app } = state;
    const {
        referrer,
        locale,
        siteName,
        brand,
        templateId,
        experiments
    } = app;
    const brandName = capitalizeBrand(brand);
    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
    const experimentsUtil = new ExperimentUtil(experiments);
    // eslint-disable-next-line max-len
    const contentMessagingUpdatesEnabled = isContentMessagingUpdatesEnabled(experimentsUtil);
    const isRebranded = isRebrandingActive(experimentsUtil);
    return {
        isRebranded,
        buttonNextLabel: translations.getTranslation('Vrbo.cta.next'),
        bedroomCount: app.lyp.bedroomCount,
        bathroomCount: app.lyp.bathroomCount,
        bathroomCountStepSize: getLinks(locale.canonical, siteName).hasHalfBath ? 0.5 : 1,
        referrer,
        translations,
        brandName,
        templateId,
        contentMessagingUpdatesEnabled
    };
};

const ConnectedBedAndBath = connect(mapStateToProps, mapDispatchToProps)(BedAndBath);
ConnectedBedAndBath.displayName = 'BedAndBath';
export default hot(module)(withPageFlow(ConnectedBedAndBath));
